import { Usuarios } from 'src/app/dashboard/models/usuarios';
import { Component, OnInit, Input } from '@angular/core';
import { AuthService } from '../shared/services/auth.service';
import { ApiService } from '../shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatosDenuncia } from 'src/app/shared/models/datosDenuncia';
import { PlazosDenuncia } from '../model/plazosDenuncia';
import { environment } from '../../environments/environment.prod';
import { DatePipe } from '@angular/common';
import { Rol } from '../model/rol';
import { forkJoin } from 'rxjs';


@Component({
  selector: 'app-enviar-denuncia-decisor',
  templateUrl: './enviar-denuncia-decisor.component.html',
  styleUrls: ['./enviar-denuncia-decisor.component.css'],
  providers: [DatePipe]
})
export class EnviarDenunciaDecisorComponent implements OnInit {
  /** Base assets URL from environment configuration. */
  assetsUrl: string = environment.baseAssetsUrl;
  /** Deadlines/plazos returned by investigator queries. */
  plazos: PlazosDenuncia[];
  /** Toggle to enable sending the complaint to decisors. */
  enviarDenuncia = false;
  /** Roles list used for redirect logic. */
  roles: Rol[];
  /** Whether a customizable complaint id is active. */
  isActiveIdPersonalizable = false;
  /** Current decisors assigned/loaded from API. */
  decisors: Usuarios[];
  /** Deadline values (strings) used to create plazos. */
  plazoFaseInvestigacionF = '';
  plazoFaseDecisorF = '';
  plazoFaseComplianceF = '';
  daysreception = '';

  @Input() denuncia: DatosDenuncia;


  constructor(private apiService: ApiService, private auth: AuthService, private router: Router, private toastr: ToastrService, private translate: TranslateService, private datepipe: DatePipe) { }

  ngOnInit(): void {
    let language = this.auth.getLanguage();
    if (language == "" || language == null) {
      language = 'es';
    }
    this.translate.setDefaultLang(language);

    // Check whether customizable complaint identifier is active
    if (this.auth.getIdPersonalizable() === '1') {
      this.isActiveIdPersonalizable = true;
    }

    this.getPlazos()

  }

  /**
   * Load deadline values for the complaint. Uses API values when available
   * and falls back to localStorage settings when not.
   */
  getPlazos(): void {
    this.apiService.searchInvestigatorByIdDenuncia(this.denuncia[0].id_denuncia, '1')
      .subscribe({
        next: (res: any) => {
          const plazos: PlazosDenuncia[] = res?.data || [];
          this.plazos = plazos;

          if (this.plazos.length > 0 && this.plazos[0].id_denuncia) {
            this.plazoFaseInvestigacionF = this.plazos[0].plazo_investigacion;
            this.plazoFaseDecisorF = this.plazos[0].plazo_decisor;
            this.plazoFaseComplianceF = this.plazos[0].plazo_compliance;
            this.daysreception = localStorage.getItem("daysreception");
          } else {
            this.plazoFaseInvestigacionF = localStorage.getItem("diasinves");
            this.plazoFaseDecisorF = localStorage.getItem("diasdecisor");
            this.plazoFaseComplianceF = localStorage.getItem("diascompliance");
            this.daysreception = localStorage.getItem("daysreception");
          }
        },
        error: () => {
          this.toastr.error(this.translate.instant('texto_890'), this.translate.instant('texto_813'));
        }
      });
  }


  /** Toggle the send-complaint flag. */
  toggleEnvioDenuncia(): void {
    this.enviarDenuncia = !this.enviarDenuncia;
  }

  /**
   * Send the complaint to decisors after performing necessary checks and actions.
   */
  sendComplaintToDecisor() {
    if (!this.enviarDenuncia) return;

    this.checkIfDecisorsAssigned().then(hasDecisors => {
      if (!hasDecisors) {
        this.toastr.error(this.translate.instant('texto_878'), this.translate.instant('texto_297'));
        return;
      }

      this.apiService.sendComplaintToDecisor(
        this.auth.getSociedad(),
        this.denuncia[0].id_denuncia,
        this.auth.getIdUser(),
        '4', '3'
      ).subscribe({
        next: (res) => {
          if (res.success && res.code === 200) {
            this.createDeadlinesForAllDecisors()
              .then(() => this.notifyDecisors())
              .then(() => this.notifyComplainant())
              .then(() => this.redirectByRole());
          } else {
            this.toastr.error(this.translate.instant('texto_925'), this.translate.instant('texto_598'));
          }
        },
        error: () => {
          this.toastr.error(this.translate.instant('texto_925'), this.translate.instant('texto_598'));
        }
      });
    });
  }


  /** Check whether there are decisors assigned to the complaint. */
  checkIfDecisorsAssigned(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.apiService.searchInvesSelect(this.auth.getSociedad(), this.denuncia[0].id_denuncia, "3").subscribe({
        next: (response) => {
          this.decisors = response?.data || [];
          resolve(this.decisors.length > 0);
        },
        error: (err) => {
          resolve(false);
        }
      });
    });
  }

  /** Create deadlines for all decisors in parallel. */
  createDeadlinesForAllDecisors(): Promise<void> {
    return new Promise(resolve => {
      const requests = this.decisors.map(decisor =>
        this.apiService.createDeadlinesByIdComplaint(
          this.auth.getSociedad(),
          this.denuncia[0].id_denuncia,
          this.plazoFaseInvestigacionF,
          this.plazoFaseDecisorF,
          this.plazoFaseComplianceF,
          this.daysreception,
          decisor.id_user
        )
      );

      forkJoin(requests).subscribe({
        next: () => resolve(),
        error: () => resolve()
      });
    });
  }


  /** Notify each decisor via email using the current plazos list. */
  notifyDecisors(): Promise<void> {
    return new Promise(resolve => {
      this.apiService.searchInvestigatorByIdDenuncia(
        this.denuncia[0].id_denuncia,
        '4'
      ).subscribe((plazos: PlazosDenuncia[]) => {
        this.plazos = plazos;

        this.plazos.forEach(p => {
          const endDate = this.getDeadlineDate(Number(this.plazos[0].plazo_decisor));
          const idShown = this.getShownComplaintId();
          const url = `${environment.base}/editar-denuncia/${this.denuncia[0].id_denuncia}`;
          const subject = `${this.translate.instant('texto_564')} ${idShown}`;
          const message =
            `${this.translate.instant('texto_576')} ${p.mail_user}` +
            `${this.translate.instant('texto_592')} ${idShown} ` +
            `${this.translate.instant('texto_593')} ${endDate}`;

          this.apiService.sendEmail(
            p.mail_user,
            subject,
            message,
            url,
            this.clausulasEmail(),
            this.translate.instant('texto_595'),
            'gestor'
          ).subscribe();
        });

        resolve();
      });
    });
  }


  /** Notify the complainant (if email provided) about the phase change. */
  notifyComplainant(): Promise<void> {
    return new Promise(resolve => {
      const email = this.denuncia[0].denunciante_correo;
      if (!email) return resolve();

      const idShown = this.getShownComplaintId();
      const url = environment.base + "/ver-denuncia/consultar-denuncia";
      const message =
        `${this.translate.instant('texto_576')} ${this.denuncia[0].denunciante_nombre}` +
        `${this.denuncia[0].denunciante_apellidos} ${this.translate.instant('texto_596')} ` +
        `${idShown} ${this.translate.instant('texto_597')}`;

      this.apiService.sendEmail(
        email,
        this.translate.instant('texto_598'),
        message,
        url,
        this.clausulasEmail(),
        this.translate.instant('texto_598'),
        'gestor'
      ).subscribe(() => resolve(), () => resolve());
    });
  }


  /** Redirect current user depending on their roles and ownership. */
  redirectByRole(): void {
    this.apiService.searchRoleByUserSociety(
      'setUser',
      this.auth.getSociedad(),
      '1'
    ).subscribe({
      next: (roles) => {
        if (roles.success && roles.code === 200) {
          const roleList = roles.data.map(r => r.id_rol).join(',');
          const isDecisor = roleList.includes("3");
          const isOwner = this.plazos[0].id_user === this.auth.getIdUser();
          const route = (isDecisor && isOwner)
            ? ['editar-denuncia/', this.denuncia[0].id_denuncia]
            : ['dashboard/complaint-management/mis-denuncias'];

          this.router.navigateByUrl('/RefreshComponent', { skipLocationChange: true })
            .then(() => this.router.navigate(route));
        }
      }
    });
  }

  /** Return a localized date string for a deadline calculated in days. */
  getDeadlineDate(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString(this.translate.currentLang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /** Return the complaint identifier that should be shown to users. */
  getShownComplaintId(): string {
    return this.isActiveIdPersonalizable
      ? this.denuncia[0].identificador_denuncia || this.denuncia[0].id_denuncia
      : this.denuncia[0].id_denuncia;
  }

  /** Read the clauses email preference from localStorage. */
  clausulasEmail(): boolean {
    return localStorage.getItem("clausulas_correo") === '1';
  }



}
